'use client'

import { useId, useState, type KeyboardEvent, type ReactNode } from 'react'

type Props = {
  initialChecked: boolean
  value?: number
  children: ReactNode
}

/**
 * Client-side wrapper around Lexical's check-list <li>. Replaces the default
 * `readOnly` checkbox with an actually-interactive one so readers can tick
 * items as they go. State is local — checks aren't persisted across reloads,
 * which matches the "informal article checklist" use case (not a todo app).
 *
 * Markup mirrors the upstream `ListJSXConverter` output so the brand-styled
 * CSS in globals.css continues to apply.
 */
export function ChecklistItem({ initialChecked, value, children }: Props) {
  const [checked, setChecked] = useState(initialChecked)
  const id = useId()

  const toggle = () => setChecked((c) => !c)

  const onKeyDown = (e: KeyboardEvent<HTMLLIElement>) => {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault()
      toggle()
    }
  }

  return (
    <li
      aria-checked={checked ? 'true' : 'false'}
      className={`list-item-checkbox ${
        checked ? 'list-item-checkbox-checked' : 'list-item-checkbox-unchecked'
      }`}
      role="checkbox"
      style={{ listStyleType: 'none' }}
      tabIndex={0}
      value={value}
      onKeyDown={onKeyDown}
    >
      <input
        checked={checked}
        id={id}
        type="checkbox"
        onChange={(e) => setChecked(e.target.checked)}
      />
      <label htmlFor={id}>{children}</label>
    </li>
  )
}
